import { useState, useEffect } from 'react';
import { useParams, useLocation as useRouterLocation, Link } from 'react-router-dom';
import { MapPin, Star, Phone, Clock, Heart, Share2, ArrowLeft, ExternalLink, Sparkles, ShoppingBag, Calendar, ChevronRight, Loader } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { restaurants, reviews } from '../mockData';
import { useToast } from '../hooks/use-toast';
import { useLocation } from '../contexts/LocationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MenuSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex gap-4 p-4 border rounded-xl animate-pulse">
        <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-3 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-200 rounded w-1/4" />
        </div>
      </div>
    ))}
  </div>
);

const CATEGORY_ORDER = ['Chef Special', 'Starters', 'Mains', 'Desserts', 'Drinks'];

const RestaurantDetail = ({ savedItems, toggleSave }) => {
  const { id } = useParams();
  const routerLocation = useRouterLocation();
  const { locationString } = useLocation();
  const { toast } = useToast();

  // Support both mock restaurants (by ID) and AI-suggested restaurants (via state)
  const mockRestaurant = restaurants.find(r => r.id === parseInt(id));
  const stateRestaurant = routerLocation.state?.restaurant;
  const restaurant = mockRestaurant || stateRestaurant || null;

  const [activeTab, setActiveTab] = useState('overview');
  const [aiDetail, setAiDetail] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const restaurantReviews = mockRestaurant ? reviews.filter(r => r.restaurantId === parseInt(id)) : [];

  // Fetch AI enhancement when menu tab is opened or page loads
  const fetchAiDetail = async () => {
    if (!restaurant || aiDetail) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/restaurants/ai-detail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: restaurant.name,
          cuisine: restaurant.cuisine || '',
          location: restaurant.location || restaurant.address || locationString || '',
          rating: restaurant.rating || 4.5,
          priceRange: restaurant.priceRange || '$$',
          description: restaurant.description || '',
        }),
      });
      if (res.ok) setAiDetail(await res.json());
    } catch { /* silent */ } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    // Auto-fetch AI detail on mount
    fetchAiDetail();
  }, [restaurant?.name]); // eslint-disable-line

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Restaurant not found</h2>
          <Link to="/foodie"><Button className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">Browse Restaurants</Button></Link>
        </div>
      </div>
    );
  }

  const isSaved = savedItems?.includes(restaurant.id);

  const handleSave = () => {
    toggleSave?.(restaurant.id);
    toast({ title: isSaved ? 'Removed from saved' : 'Saved!' });
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: restaurant.name, text: restaurant.description, url: window.location.href });
    } else {
      await navigator.clipboard.writeText(window.location.href).catch(() => {});
      toast({ title: 'Link Copied!' });
    }
  };

  const groupedMenu = aiDetail?.menu_items?.reduce((acc, item) => {
    const cat = item.popular ? 'Chef Special' : (item.category || 'Mains');
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {}) || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Back */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to={-1}>
            <Button variant="ghost" className="hover:text-[#FF6B35]">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-80 md:h-96">
        <img loading="lazy" src={restaurant.image} alt={restaurant.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className="bg-[#F4C430] text-gray-900">{restaurant.cuisine}</Badge>
              {restaurant.openNow && <Badge className="bg-green-500">Open Now</Badge>}
              {aiDetail?.ambiance?.slice(0, 2).map(a => (
                <Badge key={a} variant="outline" className="border-white/60 text-white text-xs">{a}</Badge>
              ))}
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-2">{restaurant.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-base">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#F4C430] text-[#F4C430]" />{restaurant.rating} <span className="text-white/70">({restaurant.reviews || 0} reviews)</span></span>
              <span className="text-white/50">·</span>
              <span>{restaurant.priceRange}</span>
              <span className="text-white/50">·</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{restaurant.location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6 bg-white border border-gray-200 w-full justify-start overflow-x-auto">
                {['overview', 'menu', 'reviews', 'similar'].map((tab) => (
                  <TabsTrigger key={tab} value={tab}
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#F4C430] data-[state=active]:to-[#FF8C42] data-[state=active]:text-white capitalize">
                    {tab === 'menu' ? (
                      <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" />AI Menu</span>
                    ) : tab}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Overview */}
              <TabsContent value="overview">
                <div className="space-y-6">
                  <Card>
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-3">About</h2>
                      <p className="text-gray-600 mb-4">{restaurant.description}</p>
                      {restaurant.tags && (
                        <div className="flex flex-wrap gap-2">
                          {restaurant.tags.map(tag => <Badge key={tag} variant="outline" className="border-[#FF6B35] text-[#FF6B35]">{tag}</Badge>)}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* AI review summary */}
                  {aiDetail?.review_summary && (
                    <Card className="border-[#FF6B35]/20 bg-[#FFF5F0]">
                      <CardContent className="p-6">
                        <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-[#FF6B35]" />What Regulars Say
                        </h3>
                        <p className="text-gray-700 text-sm italic">"{aiDetail.review_summary}"</p>
                        {aiDetail.best_for && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {aiDetail.best_for.map(b => (
                              <span key={b} className="text-xs bg-white border border-[#FF6B35]/30 text-[#FF6B35] px-2.5 py-1 rounded-full">{b}</span>
                            ))}
                          </div>
                        )}
                        {aiDetail.popular_time && (
                          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{aiDetail.popular_time}</p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Location */}
                  <Card>
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Location & Hours</h2>
                      <div className="space-y-3">
                        {restaurant.address && <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#FF6B35] mt-0.5" /><div><p className="font-medium text-gray-900">Address</p><p className="text-gray-600 text-sm">{restaurant.address}</p></div></div>}
                        {restaurant.hours && <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-[#FF6B35] mt-0.5" /><div><p className="font-medium text-gray-900">Hours</p><p className="text-gray-600 text-sm">{restaurant.hours}</p></div></div>}
                        {restaurant.phone && <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-[#FF6B35] mt-0.5" /><div><p className="font-medium text-gray-900">Phone</p><p className="text-gray-600 text-sm">{restaurant.phone}</p></div></div>}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* AI Menu */}
              <TabsContent value="menu">
                {aiLoading ? (
                  <div>
                    <div className="flex items-center gap-2 mb-4 text-[#FF6B35]">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span className="text-sm font-medium">AI is generating the menu...</span>
                    </div>
                    <MenuSkeleton />
                  </div>
                ) : aiDetail?.menu_items ? (
                  <div className="space-y-6">
                    {/* Chef's special first */}
                    {aiDetail.chef_special && (
                      <Card className="border-[#F4C430] bg-gradient-to-r from-[#FFF9F0] to-white overflow-hidden">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-[#F4C430] text-gray-900 text-xs">Chef's Special</Badge>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900">{aiDetail.chef_special.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{aiDetail.chef_special.description}</p>
                          <p className="text-[#FF6B35] font-bold mt-2">${aiDetail.chef_special.price}</p>
                        </CardContent>
                      </Card>
                    )}

                    {CATEGORY_ORDER.filter(c => groupedMenu[c]?.length > 0).map(category => (
                      <div key={category}>
                        <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-[#FF6B35] rounded-full inline-block" />
                          {category}
                        </h3>
                        <div className="space-y-3">
                          {groupedMenu[category].map((item, i) => (
                            <div key={i} className="flex items-start justify-between gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-[#FF6B35]/20 transition-colors">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                                  {item.popular && <Badge className="bg-[#FF6B35] text-white text-xs py-0">Popular</Badge>}
                                </div>
                                <p className="text-xs text-gray-500 mb-1.5">{item.description}</p>
                                {item.dietary?.length > 0 && (
                                  <div className="flex gap-1 flex-wrap">
                                    {item.dietary.map(d => <span key={d} className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{d}</span>)}
                                  </div>
                                )}
                              </div>
                              <span className="text-[#FF6B35] font-bold text-sm flex-shrink-0">${item.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-400 text-center pt-2 flex items-center justify-center gap-1">
                      <Sparkles className="w-3 h-3" />AI-generated menu based on cuisine type
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Sparkles className="w-12 h-12 text-[#FF6B35] mx-auto mb-3" />
                    <Button onClick={fetchAiDetail} className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">
                      Generate AI Menu
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Reviews */}
              <TabsContent value="reviews">
                {restaurantReviews.length > 0 ? (
                  <div className="space-y-4">
                    {restaurantReviews.map(review => (
                      <Card key={review.id}>
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={review.userAvatar} />
                              <AvatarFallback>{review.userName[0]}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="font-semibold text-gray-900">{review.userName}</h4>
                                  <p className="text-xs text-gray-400">{new Date(review.timestamp).toLocaleDateString()}</p>
                                </div>
                                <div className="flex items-center gap-0.5">
                                  {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-[#F4C430] text-[#F4C430]' : 'text-gray-200'}`} />
                                  ))}
                                </div>
                              </div>
                              <p className="text-gray-700 text-sm">{review.comment}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Star className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p>No reviews yet — be the first to review!</p>
                  </div>
                )}
              </TabsContent>

              {/* Similar restaurants */}
              <TabsContent value="similar">
                {aiLoading ? (
                  <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-xl animate-pulse" />)}</div>
                ) : aiDetail?.similar_restaurants?.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-2"><Sparkles className="w-4 h-4 text-[#FF6B35]" />AI-recommended similar restaurants</p>
                    {aiDetail.similar_restaurants.map((r, i) => (
                      <Card key={i} className="hover:border-[#FF6B35] transition-colors">
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            <img loading="lazy" src={r.image} alt={r.name} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900">{r.name}</h3>
                              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                <Badge variant="outline" className="text-xs border-[#FF6B35] text-[#FF6B35]">{r.cuisine}</Badge>
                                <span>{r.priceRange}</span>
                                {r.distance && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{r.distance}</span>}
                              </div>
                              <p className="text-xs text-gray-500 mt-1.5 italic">{r.why_similar}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Button onClick={fetchAiDetail} className="bg-gradient-to-r from-[#F4C430] to-[#FF6B35] text-white">
                      <Sparkles className="w-4 h-4 mr-2" />Find Similar Restaurants
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="sticky top-4">
              <CardContent className="p-6 space-y-3">
                <Button
                  className="w-full bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-semibold h-12"
                  onClick={() => window.open(`https://www.opentable.com/s/?term=${encodeURIComponent(restaurant.name)}`, '_blank')}
                >
                  <Calendar className="w-4 h-4 mr-2" />Book a Table
                </Button>
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="border-[#FF6B35] text-[#FF6B35]"
                    onClick={() => window.open(`https://www.ubereats.com/search?q=${encodeURIComponent(restaurant.name)}`, '_blank')}>
                    <ShoppingBag className="w-4 h-4 mr-1" />Order In
                  </Button>
                  <Button variant="outline" className="border-[#FF6B35] text-[#FF6B35]" onClick={handleSave}>
                    <Heart className={`w-4 h-4 mr-1 ${isSaved ? 'fill-[#F4C430]' : ''}`} />{isSaved ? 'Saved' : 'Save'}
                  </Button>
                </div>
                <Button variant="outline" className="w-full border-gray-200 text-gray-600" onClick={handleShare}>
                  <Share2 className="w-4 h-4 mr-2" />Share
                </Button>

                <div className="pt-4 border-t space-y-2">
                  <h3 className="font-semibold text-gray-900 text-sm">Quick Info</h3>
                  <div className="space-y-1.5 text-sm text-gray-600">
                    <div className="flex justify-between"><span>Cuisine</span><span className="font-medium">{restaurant.cuisine}</span></div>
                    <div className="flex justify-between"><span>Price</span><span className="font-medium">{restaurant.priceRange}</span></div>
                    <div className="flex justify-between"><span>Rating</span><span className="font-medium flex items-center gap-1"><Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" />{restaurant.rating}</span></div>
                    {restaurant.openNow !== undefined && (
                      <div className="flex justify-between"><span>Status</span><span className={`font-medium ${restaurant.openNow ? 'text-green-600' : 'text-red-500'}`}>{restaurant.openNow ? 'Open Now' : 'Closed'}</span></div>
                    )}
                  </div>
                </div>

                {/* Google Maps link */}
                {restaurant.address && (
                  <Button variant="ghost" size="sm" className="w-full text-gray-500 hover:text-[#FF6B35]"
                    onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(restaurant.address)}`, '_blank')}>
                    <ExternalLink className="w-3.5 h-3.5 mr-2" />View on Google Maps
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDetail;
